import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Quote, Users, Mailbox, Image as ImageIcon,
  FileText, Settings, LogOut, Lock,
} from "lucide-react";
import { useAdminAuth } from "@/admin/AdminAuthContext";
import { BRAND } from "@/lib/assets";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/subsidiaries", label: "Subsidiaries", icon: Building2 },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/submissions", label: "Submissions", icon: Mailbox },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-offwhite flex" data-testid="admin-layout">
      <aside className="w-64 bg-navy-dark text-white flex flex-col fixed inset-y-0 left-0">
        <Link to="/admin" className="flex items-center gap-3 px-6 py-6 border-b border-white/10" data-testid="admin-logo">
          <img src={BRAND.logo}
               alt="Salihi Group" className="h-9 brightness-0 invert" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold">Admin</div>
            <div className="font-serif text-sm">Salihi Group</div>
          </div>
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-0.5 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 text-sm rounded transition-colors ${
                  isActive
                    ? "bg-white/10 text-gold border-l-2 border-gold pl-[14px]"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-6 border-t border-white/10 pt-6 space-y-1">
          <NavLink to="/admin/change-password"
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 rounded"
            data-testid="admin-nav-change-password">
            <Lock size={16} strokeWidth={1.5} /> Change Password
          </NavLink>
          <button
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:text-gold hover:bg-white/5 rounded"
            data-testid="admin-logout"
          >
            <LogOut size={16} strokeWidth={1.5} /> Logout
          </button>
          <div className="px-4 pt-4 text-[11px] text-white/40 border-t border-white/5 mt-3">
            <div className="truncate">{user?.email}</div>
          </div>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-8 md:p-12 min-h-screen">
        {children}
      </main>
    </div>
  );
}
